import type { ValidationIssue } from '@customerio/dryad';

// Mirrors ui's app/utils/condition-builder/consent-state-condition.ts.

export const CONSENT_STATE_CONDITION_TYPE = 'consent_state';

export const CONSENT_STATE_VALUES = ['opted_in', 'opted_out'] as const;

export type ConsentStateValue = (typeof CONSENT_STATE_VALUES)[number];

export const CONSENT_STATE_CHANNELS = ['any', 'email', 'sms', 'whatsapp'] as const;

export type ConsentStateChannel = (typeof CONSENT_STATE_CHANNELS)[number];

export type ConsentStatePreferenceChannel = Exclude<ConsentStateChannel, 'any'>;

export type ConsentStateSenderChannel = 'sms' | 'whatsapp';

// `optInByDefault` is captured at pick time: the wire operator depends on it.
export type ConsentStateTarget =
  | { kind: 'none' }
  // `channelOptInByDefault` is null under Any channel.
  | { kind: 'topic'; topicId: string; optInByDefault: boolean; channelOptInByDefault: boolean | null }
  | { kind: 'channel'; optInByDefault: boolean }
  // '' is "Any number", matching the opt-out condition.
  | { kind: 'sender'; phoneNumber: string };

export type ConsentStateTopicTarget = Extract<ConsentStateTarget, { kind: 'topic' }>;

export type ConsentStateChannelTarget = Extract<ConsentStateTarget, { kind: 'channel' }>;

export type ConsentStateConditionState = {
  consent: ConsentStateValue;
  channel: ConsentStateChannel;
  target: ConsentStateTarget;
};

export function consentStateValueOf(value: string): ConsentStateValue | null {
  return CONSENT_STATE_VALUES.find((state) => state === value) ?? null;
}

export function consentStateChannelOf(value: string): ConsentStateChannel | null {
  return CONSENT_STATE_CHANNELS.find((channel) => channel === value) ?? null;
}

export function consentStateSenderChannelOf(channel: ConsentStateChannel): ConsentStateSenderChannel | null {
  return channel === 'sms' || channel === 'whatsapp' ? channel : null;
}

export const CONSENT_STATE_VALUE_OPTIONS: readonly { value: ConsentStateValue; label: string }[] = [
  { value: 'opted_in', label: 'Opted in' },
  { value: 'opted_out', label: 'Opted out' },
];

const CONSENT_STATE_CHANNEL_LABELS = {
  any: 'Any channel',
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
} satisfies Record<ConsentStateChannel, string>;

export function consentStateChannelLabel(channel: ConsentStateChannel): string {
  return CONSENT_STATE_CHANNEL_LABELS[channel];
}

function topicIssues(target: ConsentStateTopicTarget, channel: ConsentStateChannel): Required<ValidationIssue>[] {
  if (target.topicId === '') {
    return [{ message: 'Choose a topic.', field: 'target' }];
  }
  if (channel !== 'any' && target.channelOptInByDefault === null) {
    return [{ message: 'Choose a channel with a subscription setting to filter by topic.', field: 'channel' }];
  }
  return [];
}

export function validateConsentStateCondition(state: ConsentStateConditionState): Required<ValidationIssue>[] {
  const { target, channel } = state;
  switch (target.kind) {
    case 'none':
      return [{ message: 'Choose what to check consent for.', field: 'target' }];
    case 'topic':
      return topicIssues(target, channel);
    case 'channel':
      return channel === 'any' ? [{ message: 'Choose a channel.', field: 'channel' }] : [];
    case 'sender':
      return consentStateSenderChannelOf(channel) != null
        ? []
        : [{ message: 'Choose SMS or WhatsApp to filter by sender.', field: 'channel' }];
    default: {
      const _exhaustive: never = target;
      return _exhaustive;
    }
  }
}

/** Undefined means unknown, which is never flagged as out of date. */
export type ConsentStateCurrentDefaults = {
  topicOptIn: (topicId: string) => boolean | undefined;
  channelOptIn: (channel: ConsentStatePreferenceChannel) => boolean | undefined;
};

function currentChannelDefault(
  state: ConsentStateConditionState,
  defaults: ConsentStateCurrentDefaults,
): boolean | undefined {
  return state.channel === 'any' ? undefined : defaults.channelOptIn(state.channel);
}

function refreshedTopicTarget(
  target: ConsentStateTopicTarget,
  state: ConsentStateConditionState,
  defaults: ConsentStateCurrentDefaults,
): ConsentStateTopicTarget | null {
  const optInByDefault = defaults.topicOptIn(target.topicId) ?? target.optInByDefault;
  const channelOptInByDefault =
    target.channelOptInByDefault === null
      ? null
      : (currentChannelDefault(state, defaults) ?? target.channelOptInByDefault);
  if (optInByDefault === target.optInByDefault && channelOptInByDefault === target.channelOptInByDefault) {
    return null;
  }
  return { ...target, optInByDefault, channelOptInByDefault };
}

function refreshedChannelTarget(
  target: ConsentStateChannelTarget,
  state: ConsentStateConditionState,
  defaults: ConsentStateCurrentDefaults,
): ConsentStateChannelTarget | null {
  const current = currentChannelDefault(state, defaults);
  if (current == null || current === target.optInByDefault) {
    return null;
  }
  return { ...target, optInByDefault: current };
}

/** The target with current defaults, or null when none changed. */
export function refreshedConsentStateTarget(
  state: ConsentStateConditionState,
  defaults: ConsentStateCurrentDefaults,
): ConsentStateTarget | null {
  const { target } = state;
  if (target.kind === 'topic') {
    return refreshedTopicTarget(target, state, defaults);
  }
  if (target.kind === 'channel') {
    return refreshedChannelTarget(target, state, defaults);
  }
  return null;
}

function changedSubject(state: ConsentStateConditionState, defaults: ConsentStateCurrentDefaults): 'topic' | 'channel' {
  const { target } = state;
  if (target.kind !== 'topic') {
    return 'channel';
  }
  const current = defaults.topicOptIn(target.topicId);
  return current != null && current !== target.optInByDefault ? 'topic' : 'channel';
}

export function consentStateOutOfDateMessage(
  state: ConsentStateConditionState,
  defaults: ConsentStateCurrentDefaults,
): string {
  const subject = changedSubject(state, defaults);
  const profiles = state.consent === 'opted_in' ? 'opted in' : 'opted out';
  return `This ${subject}'s default subscription setting has changed since the condition was added. This filter no longer matches ${profiles} people.`;
}
